import { useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";

const BASE = process.env.PUBLIC_URL || "";
const FRAME_COUNT = 61;
const frameUrl = (i) =>
  `${BASE}/intro-frames/f_${String(i).padStart(3, "0")}.jpg`;

/**
 * Scroll-scrubbed cinematic intro built from an IMAGE SEQUENCE.
 *
 * Frames are preloaded, then swapped on scroll based on position — no video
 * decoding or seeking, so it's reliable across all browsers (incl. iOS Safari).
 * A tall transparent spacer provides the scroll distance; the page content
 * rises up and cross-fades in over the final frames.
 */
export default function ScrollVideoIntro({
  children,
  scrollMult = 3,
  title = "Your eyes deserve better.",
  subtitle = "Keep scrolling",
}) {
  const spacerRef = useRef(null);
  const layerRef = useRef(null);
  const imgRef = useRef(null);
  const titleRef = useRef(null);
  const cueRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    const spacer = spacerRef.current;
    const img = imgRef.current;
    const layer = layerRef.current;
    const content = contentRef.current;
    if (!spacer || !img) return;

    // Always begin at the very top on the first frame
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    window.scrollTo(0, 0);

    // Preload every frame so swapping is instant
    const frames = [];
    for (let i = 1; i <= FRAME_COUNT; i++) {
      const im = new Image();
      im.src = frameUrl(i);
      if (im.decode) im.decode().catch(() => {});
      frames.push(im);
    }

    let currentIndex = -1;
    let raf = null;
    const update = () => {
      raf = null;
      const S = spacer.offsetHeight || 1;
      const vh = window.innerHeight;
      const y = window.scrollY || window.pageYOffset || 0;
      const progress = Math.min(1, Math.max(0, y / S));

      // swap frame
      const idx = Math.min(FRAME_COUNT - 1, Math.round(progress * (FRAME_COUNT - 1)));
      if (idx !== currentIndex) {
        currentIndex = idx;
        const src = frames[idx] && frames[idx].src;
        if (src) img.src = src;
      }

      // intro overlay fades early
      if (titleRef.current) {
        titleRef.current.style.opacity = String(Math.max(0, 1 - progress / 0.4));
        titleRef.current.style.transform = `translateY(${progress * -30}px)`;
      }
      if (cueRef.current) {
        cueRef.current.style.opacity = String(Math.max(0, 1 - progress / 0.25));
      }

      // reveal window = final viewport of scroll (the last frames)
      const revealStart = Math.max(0, (S - vh) / S);
      let reveal = (progress - revealStart) / (1 - revealStart || 1);
      reveal = Math.min(1, Math.max(0, reveal));
      const eased = reveal * reveal * reveal * (reveal * (reveal * 6 - 15) + 10);
      if (content) content.style.opacity = String(eased);
      if (layer) layer.style.visibility = reveal >= 0.999 ? "hidden" : "visible";
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [scrollMult]);

  return (
    <>
      {/* Fixed cinematic backdrop */}
      <div ref={layerRef} className="pointer-events-none fixed inset-0 z-0 overflow-hidden bg-black">
        <img
          ref={imgRef}
          src={frameUrl(1)}
          alt=""
          aria-hidden="true"
          data-testid="intro-frame"
          className="h-full w-full object-cover object-center"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />

        <div ref={titleRef} className="pointer-events-none absolute inset-x-0 top-[38%] px-6 text-center">
          <h2 className="mx-auto max-w-3xl font-fredoka text-4xl font-bold leading-tight tracking-tight text-white drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)] sm:text-6xl">
            {title}
          </h2>
        </div>

        <div ref={cueRef} className="pointer-events-none absolute inset-x-0 bottom-8 flex flex-col items-center gap-2 text-white/90">
          <span className="font-dm-sans text-sm font-medium tracking-wide drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
            {subtitle}
          </span>
          <ChevronDown className="h-6 w-6 animate-bounce drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]" />
        </div>
      </div>

      {/* Transparent scroll spacer that drives the scrub */}
      <div ref={spacerRef} aria-hidden style={{ height: `${scrollMult * 100}vh` }} />

      {/* The site — rises up and cross-fades in over the final frames */}
      <div ref={contentRef} className="relative z-10">
        {children}
      </div>
    </>
  );
}
